import { IServiceCurrency } from '../../../../core/models/service-currency.model';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { BehaviorSubject, finalize, of, Subscription, switchMap } from 'rxjs';
import {
  ExtractHttpErrorResponseMessage,
  FilterFieldsToQueryParamsBuilder,
  IFilterAndPaginationFieldsBase,
  ToastService,
  updateApiFilter,
} from '@irembo-andela/irembogov3-common';
import { IFieldFormGroup } from '../../../../core/models/service-change-requests-form-fields.model';
import { IPriceLogic } from '../../../../core/models/price-logic.model';
import { IPriceLogicParameter } from '../../../../core/models/price-logic-parameter.model';

interface IFilterFields extends IFilterAndPaginationFieldsBase {
  name: string | null;
  keywords: string | null;
}

@Component({
  selector: 'irembogov-configure-service-price-logic-handler',
  templateUrl: './configure-service-price-logic-handler.component.html',
  styleUrls: ['./configure-service-price-logic-handler.component.scss'],
})
export class ConfigureServicePriceLogicHandlerComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() label = '';
  @Input() formFields!: any;
  @Input() organizationId!: string;
  @Input() defaultData: any = null;
  @Output() addScenarios: EventEmitter<any> = new EventEmitter<any>();

  priceLogicForm!: FormGroup;
  isLoadingServiceCurrencies = false;
  serviceCurrenciesBuffer: IServiceCurrency[] = [];
  serviceCurrenciesBufferCollection = 0;
  formFieldsValues: IFieldFormGroup[] = [];

  priceLogics: IPriceLogic[] = [];
  isLoadingPriceLogics = false;
  priceLogicParameters: IPriceLogicParameter[] = [];
  isLoadingPriceLogicParameters = false;
  parameterMappingTypes: Record<string, string> = {};

  priceFormulas: any[] = [];
  isLoadingPriceFormulas = false;

  selectedPriceLogic: IPriceLogic | null = null;
  selectedFormula: any = null;
  numberOnlyField: IFieldFormGroup[] = [];

  // Cache for saved parameter mappings
  savedParameterMappings: Record<string, any> = {};

  _filterObject: BehaviorSubject<IFilterFields> =
    new BehaviorSubject<IFilterFields>({
      page: 0,
      size: 8,
      name: '',
      keywords: '',
    });
  serviceCurrenciesSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this._filterObject
      .asObservable()
      .pipe(
        switchMap((filter: IFilterFields) => {
          const queryParams: string =
            FilterFieldsToQueryParamsBuilder<IFilterFields>(filter);
          this.getServiceCurrencies(queryParams, filter.page ?? 0);
          return of();
        })
      )
      .subscribe();

    this.createForm();

    // If we have defaultData with priceLogicCode, we'll use it to select the price logic
    const hasPriceLogicData =
      this.defaultData &&
      this.defaultData.priceType === 'PRICE_LOGIC' &&
      this.defaultData.priceLogicCode;

    // Cache the parameter mappings if they exist
    if (hasPriceLogicData && this.defaultData.priceParameterMapping) {
      this.savedParameterMappings = {
        ...this.defaultData.priceParameterMapping,
      };
    }

    if (hasPriceLogicData) {
      this.getPriceLogics(
        this.defaultData.priceLogicCode,
        this.defaultData.formulaCode
      );
    } else {
      this.getPriceLogics();
    }

    // Subscribe to form changes to emit data
    this.priceLogicForm.valueChanges.subscribe(values => {
      if (this.priceLogicForm.valid) {
        this.emitPriceLogicData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields']?.currentValue) {
      this.formFields = changes['formFields'].currentValue;
      this.formFieldsValues = [];
      this.numberOnlyField = [];
      this.formFields?.fields?.forEach((field: any) => {
        field?.fieldGroup.forEach((fieldGroup: any) => {
          fieldGroup.fieldGroup.forEach((fg: any) => {
            fg.fieldGroup.forEach((element: IFieldFormGroup) => {
              if (
                element?.type === 'number' ||
                element?.props?.pattern === '^\\d*$'
              ) {
                this.numberOnlyField.push(element);
              }
              if (
                element?.type === 'customcascadingdropdowns' &&
                element.props?.configs
              ) {
                element.props.configs.forEach((config: any) => {
                  config['type'] = 'cascading_customdropdown';
                  this.formFieldsValues.push(config);
                });
                return;
              }
              return this.formFieldsValues.push(element);
            });
          });
        });
      });
    }
  }

  /**
   * Creates the main form
   */
  createForm() {
    // Initialize with default values if available
    let defaultCurrency = '';

    if (this.defaultData && this.defaultData.priceType === 'PRICE_LOGIC') {
      defaultCurrency = this.defaultData.baseCurrency || '';
    }

    this.priceLogicForm = this.fb.group({
      selectedPriceLogic: [null, Validators.required],
      selectedFormula: [null],
      serviceCurrency: [defaultCurrency, Validators.required],
    });
  }

  /**
   * Fetches price logics
   */
  getPriceLogics(priceLogicCode = '', priceFormulaCode = '') {
    this.isLoadingPriceLogics = true;
    this.serviceCurrenciesSubscription = this.serviceChangeRequestService
      .getPriceLogics(this.organizationId)
      .pipe(finalize(() => (this.isLoadingPriceLogics = false)))
      .subscribe({
        next: response => {
          this.priceLogics = response?.data;

          // If we have default data with a price logic code, find and select it
          if (priceLogicCode !== '') {
            const savedPriceLogic = this.priceLogics.find(
              logic => logic.code === priceLogicCode
            );

            if (savedPriceLogic) {
              // Set the selected price logic
              this.priceLogicForm
                .get('selectedPriceLogic')
                ?.setValue(savedPriceLogic);
              this.updateSelectedPriceLogic(savedPriceLogic, priceFormulaCode);
            }
          }
        },
        error: err => {
          this.handleServiceCurrenciesError('Error fetching price logics', err);
        },
      });
  }

  /**
   * Updates selected price logic and fetches its parameters
   */
  updateSelectedPriceLogic(event: IPriceLogic, priceFormulaCode = '') {
    // Clear previous parameters
    this.clearPriceLogicParameters();

    this.selectedPriceLogic = event;
    this.getPriceLogicParameters();

    // Only reset formula if we're changing to a different price logic than what was loaded initially
    const shouldResetFormula =
      this.selectedFormula != null &&
      (priceFormulaCode == '' ||
        this.selectedFormula.code !== priceFormulaCode);

    if (shouldResetFormula) {
      // Reset formula selection when price logic changes
      this.priceLogicForm.get('selectedFormula')?.setValue(null);
      this.selectedFormula = null;
    }

    // Load formulas and set the selected one if it matches the default
    this.getPriceFormulas(priceFormulaCode);
  }

  updateSelectedFormula(event: any) {
    this.priceLogicForm.get('selectedFormula')?.setValue(event);
    this.selectedFormula = event;

    // When formula is updated, emit new price logic data
    this.emitPriceLogicData();
  }

  /**
   * Fetches price formulas for the selected price logic
   */
  getPriceFormulas(priceFormulaCode = '') {
    if (!this.selectedPriceLogic) {
      return;
    }

    this.isLoadingPriceFormulas = true;
    this.serviceCurrenciesSubscription = this.serviceChangeRequestService
      .getPriceFormulas(this.selectedPriceLogic?.id, { deactivated: false })
      .pipe(finalize(() => (this.isLoadingPriceFormulas = false)))
      .subscribe({
        next: response => {
          this.priceFormulas = response?.data || [];

          // Check if we have a saved formula code from defaultData
          if (priceFormulaCode !== '') {
            const savedFormula = this.priceFormulas.find(
              formula => formula.code === priceFormulaCode
            );

            if (savedFormula) {
              this.updateSelectedFormula(savedFormula);
            }
          }
        },
        error: err => {
          this.handleServiceCurrenciesError(
            'Error fetching price formulas',
            err
          );
        },
      });
  }

  /**
   * Fetches price logic parameters
   */
  getPriceLogicParameters() {
    if (!this.selectedPriceLogic) {
      return;
    }

    this.isLoadingPriceLogicParameters = true;
    this.serviceCurrenciesSubscription = this.serviceChangeRequestService
      .getPriceLogicParameters(this.selectedPriceLogic?.id)
      .pipe(finalize(() => (this.isLoadingPriceLogicParameters = false)))
      .subscribe({
        next: response => {
          this.priceLogicParameters = response?.data;

          // Check if we have saved parameters (either from defaultData or our cache)
          const hasSavedParams =
            Object.keys(this.savedParameterMappings).length > 0 ||
            (this.defaultData &&
              this.defaultData.priceType === 'PRICE_LOGIC' &&
              this.defaultData.priceParameterMapping &&
              Object.keys(this.defaultData.priceParameterMapping).length > 0);

          // Use the cache as the source of saved parameters if it has values
          if (hasSavedParams) {
            if (
              Object.keys(this.savedParameterMappings).length === 0 &&
              this.defaultData?.priceParameterMapping
            ) {
              this.savedParameterMappings = {
                ...this.defaultData.priceParameterMapping,
              };
            }
          }

          this.addPriceLogicParametersToForm(hasSavedParams);
        },
        error: err => {
          this.handleServiceCurrenciesError(
            'Error fetching price logic parameters',
            err
          );
        },
      });
  }

  /**
   * Adds price logic parameters to the form dynamically
   */
  addPriceLogicParametersToForm(hasSavedParams = false) {
    // Add form controls for each parameter
    this.priceLogicParameters.forEach(param => {
      const paramCode = param.code;
      const mappingTypeControl = 'mapType_' + paramCode;
      const fieldMappingControl = 'paramField_' + paramCode;
      const constValueControl = 'paramConst_' + paramCode;

      // Check if we have saved mapping for this parameter
      let savedMapping = null;
      let mappingType = 'field'; // Default mapping type

      // First check our cache, then fall back to defaultData if needed
      if (hasSavedParams) {
        if (this.savedParameterMappings[paramCode]) {
          savedMapping = this.savedParameterMappings[paramCode];
        } else if (this.defaultData?.priceParameterMapping?.[paramCode]) {
          savedMapping = this.defaultData.priceParameterMapping[paramCode];
        }

        // Check if it's a constant (format: 'constant:VALUE')
        if (
          typeof savedMapping === 'string' &&
          savedMapping.startsWith('constant:')
        ) {
          mappingType = 'constant';
          // Extract the constant value (remove 'constant:' prefix)
          savedMapping = savedMapping.substring(9);
        }
      }

      // Set default mapping type based on saved data
      this.parameterMappingTypes[paramCode] = mappingType;

      // Add mapping type control
      if (!this.priceLogicForm.contains(mappingTypeControl)) {
        this.priceLogicForm.addControl(
          mappingTypeControl,
          this.fb.control(mappingType)
        );
      }

      // Add field mapping control with saved value if available
      if (!this.priceLogicForm.contains(fieldMappingControl)) {
        this.priceLogicForm.addControl(
          fieldMappingControl,
          this.fb.control(
            mappingType === 'field' && savedMapping ? savedMapping : '',
            // Only apply required validator if this is the active mapping type
            mappingType === 'field' ? [Validators.required] : []
          )
        );
      }

      // Initialize constant value based on parameter type
      let defaultConstValue: any = null;
      switch (param.paramType.toLowerCase()) {
        case 'number':
          defaultConstValue =
            mappingType === 'constant' && savedMapping
              ? parseFloat(savedMapping)
              : 0;
          break;
        case 'boolean':
          defaultConstValue =
            mappingType === 'constant' && savedMapping
              ? savedMapping === 'true'
              : false;
          break;
        default:
          defaultConstValue =
            mappingType === 'constant' && savedMapping ? savedMapping : '';
          break;
      }

      // Add constant value control
      if (!this.priceLogicForm.contains(constValueControl)) {
        this.priceLogicForm.addControl(
          constValueControl,
          this.fb.control(
            defaultConstValue,
            // Only apply required validator if this is the active mapping type
            mappingType === 'constant' ? [Validators.required] : []
          )
        );
      }

      // Subscribe to mapping type changes
      this.priceLogicForm
        .get(mappingTypeControl)
        ?.valueChanges.subscribe(value => {
          this.parameterMappingTypes[paramCode] = value;

          // Update validation based on selected type
          if (value === 'field') {
            this.priceLogicForm
              .get(fieldMappingControl)
              ?.setValidators([Validators.required]);
            this.priceLogicForm.get(constValueControl)?.clearValidators();
          } else {
            this.priceLogicForm.get(fieldMappingControl)?.clearValidators();
            this.priceLogicForm
              .get(constValueControl)
              ?.setValidators([Validators.required]);
          }

          // Update validations
          this.priceLogicForm
            .get(fieldMappingControl)
            ?.updateValueAndValidity();
          this.priceLogicForm.get(constValueControl)?.updateValueAndValidity();
        });
    });
  }

  /**
   * Gets the current mapping type for a parameter
   */
  getParameterMappingType(paramCode: string): string {
    return this.parameterMappingTypes[paramCode] || 'field';
  }

  /**
   * Clears previous price logic parameters
   */
  clearPriceLogicParameters() {
    // Remove old parameter controls
    this.priceLogicParameters.forEach(param => {
      const mappingTypeControl = 'mapType_' + param.code;
      const fieldMappingControl = 'paramField_' + param.code;
      const constValueControl = 'paramConst_' + param.code;

      // Remove all controls
      if (this.priceLogicForm.contains(mappingTypeControl)) {
        this.priceLogicForm.removeControl(mappingTypeControl);
      }

      if (this.priceLogicForm.contains(fieldMappingControl)) {
        this.priceLogicForm.removeControl(fieldMappingControl);
      }

      if (this.priceLogicForm.contains(constValueControl)) {
        this.priceLogicForm.removeControl(constValueControl);
      }
    });

    // Reset parameters array and mapping types
    this.priceLogicParameters = [];
    this.parameterMappingTypes = {};
  }

  /**
   * Gets form fields that are compatible with the given parameter type
   */
  getCompatibleFields(param: IPriceLogicParameter): IFieldFormGroup[] {
    // Filter fields based on compatibility with the parameter type
    switch (param.paramType.toLowerCase()) {
      case 'number':
        // Return only number fields
        return this.numberOnlyField;
      case 'boolean':
        // Return checkbox fields
        return this.formFieldsValues.filter(
          field =>
            field.type === 'checkbox' ||
            (field.type === 'radio' && field.props?.options?.length === 2)
        );
      case 'select':
        // Return dropdown fields
        return this.formFieldsValues.filter(
          field =>
            field.type === 'customdropdown' ||
            field.type === 'radio' ||
            field.type === 'multicheckbox'
        );
      case 'text':
        // Return text fields
        return this.formFieldsValues.filter(
          field => field.type === 'input' || field.type === 'textarea'
        );
      default:
        // Return all fields for other parameter types
        return this.formFieldsValues;
    }
  }

  /**
   * Emits price logic data to parent component
   */
  emitPriceLogicData() {
    console.log(
      'Emitting price logic data with selected price logic and formula',
      {
        valid: this.priceLogicForm.valid,
        selectedPriceLogic: this.selectedPriceLogic,
        errors: this.priceLogicForm.errors,
        controls: Object.keys(this.priceLogicForm.controls)
          .filter(key => this.priceLogicForm.get(key)?.invalid)
          .map(key => {
            return { key, errors: this.priceLogicForm.get(key)?.errors };
          }),
      }
    );

    // Must have a selected price logic
    if (!this.selectedPriceLogic) {
      console.log('No price logic selected, cannot emit');
      return;
    }

    if (!this.priceLogicForm.valid || !this.selectedPriceLogic) {
      return;
    }

    // Create parameter mapping object
    const parameterMapping = this.createParameterMapping();

    // Create output in required format
    const outputData = {
      priceType: 'PRICE_LOGIC',
      baseValue: 0,
      baseCurrency: this.priceLogicForm.get('serviceCurrency')?.value || '',
      dynamicConditions: [], // Empty as not needed for price logic
      valueBasedOnRepetitiveComponent: false,
      repetitiveComponentKey: '',
      priceLogicCode: this.selectedPriceLogic.code,
      formulaCode: this.selectedFormula ? this.selectedFormula.code : undefined,
      priceParameterMapping: parameterMapping,
    };

    // Emit data to parent
    this.addScenarios.emit(outputData);
  }

  /**
   * Creates parameter mapping object
   */
  createParameterMapping(): Record<string, any> {
    const parameterMapping: Record<string, any> = {};

    // Process each parameter
    this.priceLogicParameters.forEach(param => {
      const paramCode = param.code;
      const mappingType = this.getParameterMappingType(paramCode);

      if (mappingType === 'field') {
        // Get form field mapping
        const fieldKey = this.priceLogicForm.get(
          'paramField_' + paramCode
        )?.value;
        if (fieldKey) {
          parameterMapping[paramCode] = fieldKey;
        }
      } else {
        // Get constant value
        const constValue = this.priceLogicForm.get(
          'paramConst_' + paramCode
        )?.value;

        // For constants, we create a special format: "constant:VALUE"
        if (constValue !== null && constValue !== undefined) {
          // Boolean values need to be converted to literal 'true' or 'false'
          const constantPrefix =
            param.paramType.toLowerCase() === 'boolean'
              ? `constant:${constValue.toString()}`
              : `constant:${constValue}`;

          parameterMapping[paramCode] = constantPrefix;
        }
      }
    });

    return parameterMapping;
  }

  /**
   * Handle service currencies API call
   */
  fetchServiceCurrencies() {
    const page = this._filterObject.getValue().page ?? 0;
    const pageSize = this._filterObject.getValue().size ?? 8;

    if ((page + 1) * pageSize < this.serviceCurrenciesBufferCollection) {
      updateApiFilter<IFilterFields>(this._filterObject, { page: page + 1 });
    }
  }

  onSearchServiceCurrency(query: { term: string }) {
    updateApiFilter<IFilterFields>(this._filterObject, {
      page: 0,
      name: query?.term,
    });
  }

  getServiceCurrencies(queryString: string, page: number) {
    this.isLoadingServiceCurrencies = true;
    this.serviceCurrenciesSubscription = this.serviceChangeRequestService
      .getServiceCurrencies(queryString)
      .pipe(finalize(() => (this.isLoadingServiceCurrencies = false)))
      .subscribe({
        next: response => {
          this.handleServiceCurrenciesResponse(response, page);
        },
        error: err => {
          this.handleServiceCurrenciesError(
            'Error getting service currencies',
            err
          );
        },
      });
  }

  handleServiceCurrenciesResponse(response: any, page: number) {
    this.serviceCurrenciesBuffer =
      page > 0
        ? this.serviceCurrenciesBuffer?.concat(response?.data?.content)
        : response?.data?.content;
    this.serviceCurrenciesBufferCollection =
      response.data.totalPages * response.data.size;
  }

  handleServiceCurrenciesError(msg: string, err: any) {
    const errorMessage = ExtractHttpErrorResponseMessage(err, msg);
    this.toastService.show({ body: errorMessage, type: 'error' });
  }

  ngOnDestroy() {
    if (this.serviceCurrenciesSubscription) {
      this.serviceCurrenciesSubscription.unsubscribe();
    }
  }
}
